/**
 * Stripe API Service
 * Handles communication with backend for Stripe subscription management.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL, CONTACT_API_URL } from '@/lib/apiConfig';

export interface CreateSubscriptionRequest {
  email: string;
  fullName: string;
  paymentMethodId: string;
  priceId: string;
}

export interface CreateSubscriptionResponse {
  subscriptionId: string;
  clientSecret: string | null;
  status: 'active' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
  customerId: string;
}

export interface SubscriptionStatus {
  subscriptionId: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface CreateCheckoutSessionRequest {
  email: string;
  fullName?: string;
  plan?: 'starter' | 'pro';
  successUrl?: string;
  cancelUrl?: string;
  customerId?: string;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

class StripeApiService extends BaseApiService {
  private publicBaseUrl: string;

  constructor() {
    super(API_URL, 'StripeApi');
    this.publicBaseUrl = CONTACT_API_URL;
  }

  /**
   * Create a Stripe Checkout Session.
   * Uses the public (unauthenticated) API endpoint.
   */
  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    this.log.debug('Creating checkout session', { email: request.email });

    const response = await fetch(`${this.publicBaseUrl}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create checkout session', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create checkout session');
    }

    const data = await response.json();
    this.log.info('Checkout session created', { sessionId: data.sessionId });
    return data;
  }

  /**
   * Create a subscription for a new user (during signup).
   * Uses the public (unauthenticated) API endpoint.
   * @deprecated Use createCheckoutSession for automatic currency support
   */
  async createSubscription(request: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
    this.log.debug('Creating subscription', { email: request.email, priceId: request.priceId });

    const response = await fetch(`${this.publicBaseUrl}/stripe/create-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create subscription', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create subscription');
    }

    const data = await response.json();
    this.log.info('Subscription created', { subscriptionId: data.subscriptionId, status: data.status });
    return data;
  }

  /**
   * Confirm a subscription payment (if 3D Secure is required).
   * Uses the public (unauthenticated) API endpoint.
   */
  async confirmSubscription(subscriptionId: string, paymentIntentId: string): Promise<{ status: string }> {
    this.log.debug('Confirming subscription', { subscriptionId, paymentIntentId });

    const response = await fetch(`${this.publicBaseUrl}/stripe/confirm-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscriptionId, paymentIntentId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to confirm subscription', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to confirm subscription');
    }

    return response.json();
  }

  /**
   * Get subscription status for authenticated user.
   * Uses the authenticated API endpoint.
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
    this.log.debug('Getting subscription status');
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/stripe/subscription-status`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get subscription status', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get subscription status');
    }

    return response.json();
  }

  /**
   * Cancel subscription at period end.
   * Uses the authenticated API endpoint.
   */
  async cancelSubscription(): Promise<{ success: boolean }> {
    this.log.debug('Canceling subscription');
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/stripe/cancel-subscription`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to cancel subscription', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to cancel subscription');
    }

    this.log.info('Subscription canceled');
    return response.json();
  }
}

// Export singleton instance
export const stripeApi = new StripeApiService();

// Stripe publishable key from environment
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

// Pro plan price ID
export const PRO_PLAN_PRICE_ID = import.meta.env.REACT_APP_STRIPE_PRO_PRICE_ID || 'price_pro_monthly';
